/**
 * Form inputs.
 *
 * Numeric inputs get `.num` (tabular, slashed zero) and right alignment.
 * A column of figures that does not line up on the decimal point cannot be
 * scanned, and a 0 that looks like an O in an order id has a cost.
 */

import { Search, X } from 'lucide-react';
import {
  forwardRef,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';

import { cn } from '@/lib/cn';

import { FIELD_SIZES, type FieldSize } from './types';

const FIELD_BASE =
  'w-full rounded border border-border bg-surface-inset px-2 text-hi placeholder:text-lo ' +
  'transition-colors outline-none focus:border-accent ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

export interface FieldProps {
  label?: ReactNode;
  /** Rendered under the field in muted type. Explain the units. */
  hint?: ReactNode;
  /** Replaces the hint and turns the border red. */
  error?: string | null;
  required?: boolean;
  className?: string;
  children: ReactNode;
  htmlFor?: string;
}

export function Field({
  label,
  hint,
  error,
  required,
  className,
  children,
  htmlFor,
}: FieldProps) {
  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label && (
        <label
          htmlFor={htmlFor}
          className="flex items-center gap-1 text-[11px] font-medium text-mid"
        >
          {label}
          {required && (
            <span className="text-danger" aria-hidden>
              *
            </span>
          )}
        </label>
      )}
      {children}
      {error ? (
        <p className="text-[11px] text-danger">{error}</p>
      ) : (
        hint && <p className="text-[11px] text-lo">{hint}</p>
      )}
    </div>
  );
}

/**
 * A text/number field with its label, hint and error.
 *
 * @param numeric  Tabular figures, right-aligned. Use for every price,
 *                 quantity and percentage — a column of numbers that does
 *                 not line up on the decimal point cannot be scanned.
 * @param suffix   Unit inside the field on the right: `%`, `USDT`, `bps`.
 *                 Always state the unit; a bare number in a risk form is
 *                 how 1% becomes 1 basis point.
 * @param prefix   Adornment inside the field on the left.
 * @param size     `xs` | `sm` | `md` — the shared control scale.
 * @param error    Replaces the hint and turns the border red.
 */
export interface InputProps
  // `prefix` is a real (and useless here) HTML attribute typed as `string`,
  // so it is omitted rather than shadowed — otherwise every consumer gets a
  // confusing variance error instead of a ReactNode adornment.
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  /** Tabular figures, right-aligned. Use for every price and quantity. */
  numeric?: boolean;
  /** Unit shown inside the field on the right: `%`, `USDT`, `bps`. */
  suffix?: ReactNode;
  /** Adornment inside the field on the left. */
  prefix?: ReactNode;
  /** The shared control scale. Matches Button and Select at every step. */
  size?: FieldSize;
  /** @deprecated Use `size`. Kept so existing call sites keep compiling. */
  inputSize?: FieldSize;
  containerClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    label,
    hint,
    error,
    numeric = false,
    suffix,
    prefix,
    size,
    inputSize,
    className,
    containerClassName,
    id,
    ...rest
  },
  ref,
) {
  const generated = useId();
  const inputId = id ?? generated;
  const resolvedSize: FieldSize = size ?? inputSize ?? 'md';

  // ── Typing a number should not be a fight ────────────────────────────
  //
  // Call sites clamp on every keystroke — `clamp(e.target.value, 1, 1000,
  // previous)`. On a controlled input that means the value is rewritten
  // between keypresses: clear the box to type "15" and it snaps back to
  // the minimum before the "5" lands, so the old digits have to be picked
  // off one at a time around a value that keeps reappearing.
  //
  // So a numeric field holds its own draft while focused and reports it
  // once, on blur or Enter. The clamp still runs — just when the number
  // is finished rather than halfway through typing it. Escape abandons
  // the edit.
  // ── The suffix must not sit on top of the value ──────────────────────
  //
  // The suffix is absolutely positioned, so it takes no layout space and
  // the input needs padding to clear it. That padding used to be a fixed
  // `pr-9` (2.25rem) — fine for `%`, far too narrow for `USDT` or
  // `% of account value`, so a right-aligned number ran underneath and
  // "100" appeared inside "% of account value".
  //
  // Padding follows the suffix's real width instead of a guess. Measured
  // in useLayoutEffect so it lands before paint, and re-measured when the
  // suffix or the control size changes.
  const suffixRef = useRef<HTMLSpanElement>(null);
  const [suffixWidth, setSuffixWidth] = useState(0);

  useLayoutEffect(() => {
    if (!suffix) {
      setSuffixWidth(0);
      return;
    }
    const measure = () => setSuffixWidth(suffixRef.current?.offsetWidth ?? 0);
    measure();
    // Fonts land after first paint and change the width under us.
    const observer = new ResizeObserver(measure);
    if (suffixRef.current) observer.observe(suffixRef.current);
    return () => observer.disconnect();
  }, [suffix, resolvedSize]);

  //: 8px clears the suffix's own `right-2`, 6px is breathing room.
  const suffixPadding = suffixWidth ? suffixWidth + 14 : undefined;

  const isNumber = rest.type === 'number';
  const [draft, setDraft] = useState<string | null>(null);
  const committed = rest.value ?? rest.defaultValue ?? '';

  const commit = (raw: string, target: HTMLInputElement) => {
    setDraft(null);
    if (raw !== String(committed)) {
      const event = {
        ...({} as ChangeEvent<HTMLInputElement>),
        target: Object.assign(target, { value: raw }),
        currentTarget: target,
      } as ChangeEvent<HTMLInputElement>;
      rest.onChange?.(event);
    }
  };

  const numberProps: Partial<InputHTMLAttributes<HTMLInputElement>> = isNumber
    ? {
        // Free text under the hood: no browser step-snapping, no spinner
        // arrows, and a decimal keypad on touch.
        type: 'text',
        inputMode: 'decimal',
        value: draft ?? String(committed),
        onChange: (event) => setDraft(event.target.value),
        onFocus: (event) => {
          setDraft(String(committed));
          event.target.select();      // typing replaces, never appends
          rest.onFocus?.(event);
        },
        onBlur: (event) => {
          commit(event.target.value, event.target);
          rest.onBlur?.(event);
        },
        onKeyDown: (event) => {
          if (event.key === 'Enter') {
            commit(event.currentTarget.value, event.currentTarget);
            event.currentTarget.blur();
          } else if (event.key === 'Escape') {
            setDraft(null);
            event.currentTarget.blur();
          }
          rest.onKeyDown?.(event);
        },
      }
    : {};

  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={rest.required}
      htmlFor={inputId}
      className={containerClassName}
    >
      <div className="relative flex items-center">
        {prefix && (
          <span className="pointer-events-none absolute left-2 text-[11px] text-lo">{prefix}</span>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          className={cn(
            FIELD_BASE,
            FIELD_SIZES[resolvedSize],
            numeric && 'num text-right',
            Boolean(prefix) && 'pl-6',
            // pr-9 is only the fallback for the first paint, before the
            // suffix has been measured; the inline style below wins.
            Boolean(suffix) && 'pr-9',
            error && 'border-danger focus:border-danger',
            className,
          )}
          {...rest}
          {...numberProps}
          style={
            suffixPadding
              ? { ...rest.style, paddingRight: `${suffixPadding}px` }
              : rest.style
          }
        />
        {suffix && (
          <span
            ref={suffixRef}
            className="pointer-events-none absolute right-2 text-[11px] text-lo"
          >
            {suffix}
          </span>
        )}
      </div>
    </Field>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  containerClassName?: string;
  /** Live `n / max` counter. The backend enforces minimum reason lengths on
   *  every audited action, so the form should say so before submitting. */
  showCount?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, className, containerClassName, showCount, id, value, maxLength, ...rest },
  ref,
) {
  const generated = useId();
  const textareaId = id ?? generated;
  const length = typeof value === 'string' ? value.length : 0;

  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={rest.required}
      htmlFor={textareaId}
      className={containerClassName}
    >
      <div className="relative">
        <textarea
          ref={ref}
          id={textareaId}
          value={value}
          maxLength={maxLength}
          aria-invalid={error ? true : undefined}
          className={cn(
            FIELD_BASE,
            'min-h-[72px] resize-y py-1.5 text-[13px] leading-relaxed',
            error && 'border-danger focus:border-danger',
            className,
          )}
          {...rest}
        />
        {showCount && (
          <span className="num pointer-events-none absolute right-2 bottom-1.5 text-[10px] text-lo">
            {length}
            {maxLength ? ` / ${maxLength}` : ''}
          </span>
        )}
      </div>
    </Field>
  );
});

export interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  onClear?: () => void;
  containerClassName?: string;
}

/**
 * Search box with a clear button.
 *
 * The magnifier is decorative, so the field needs a real name. Pass a
 * specific `aria-label` ("Filter trades") where there is more than one
 * search box on a page; the default is only better than nothing.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  function SearchInput({ onClear, value, className, containerClassName, ...rest }, ref) {
    return (
      <div className={cn('relative flex items-center', containerClassName)}>
        <Search size={13} className="pointer-events-none absolute left-2 text-lo" aria-hidden />
        <input
          ref={ref}
          type="search"
          value={value}
          aria-label={rest['aria-label'] ?? 'Search'}
          className={cn(FIELD_BASE, FIELD_SIZES.sm, 'pr-7 pl-7', className)}
          {...rest}
        />
        {onClear && typeof value === 'string' && value.length > 0 && (
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear search"
            className="absolute right-1.5 rounded p-0.5 text-lo hover:bg-surface-3 hover:text-hi"
          >
            <X size={12} />
          </button>
        )}
      </div>
    );
  },
);
